import { Auth0ContextInterface } from "@auth0/auth0-react";
import { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Params,
  useLoaderData,
  useOutletContext,
  useParams,
} from "react-router-dom";
import {
  FarmAnnums,
  FarmGet,
  getAnnumsForFarm,
  getFarmById,
} from "regen-api-ts-client";
import {
  getAnnumsForFarmOptions,
  getFarmByIdOptions,
} from "regen-api-ts-client/react-query";

import { useRegenQuery } from "../util/apiHooks";
import { useMobileNavOutletContext } from "./layouts/Layout";

// Loader for the part of the app which displays a specific farm (set via url params).
export const farmDataLoader =
  (queryClient: QueryClient, authData: Auth0ContextInterface) =>
  async ({ params }: { params: Params<"farmId"> }) => {
    const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } =
      authData;
    try {
      if (!isAuthenticated) {
        await getAccessTokenSilently();
      }
    } catch (e) {
      // We shouldn't hit this case unless an unauthed user is trying to access
      // farm pages, in which case they'll be redirected by the route guards. But
      // since data loaders fire before route guards (even if they are children
      // of the route guards), we'll still throw this error.
      // Testable by signing out in a different tab and then refreshing this tab.
      loginWithRedirect();
      return Promise.reject("User not authenticated.");
    }
    // Don't query for data until there is a valid farm id in the params.
    if (!params.farmId) {
      // TODO add a redirect to the router instead of returning an error here.
      return Promise.reject("Farm id not in params.");
    }

    // Query for farm and annums to determine whether they will return data. If the queries succeed, load the child routes.
    const farmPromise = queryClient.ensureQueryData(
      getFarmByIdOptions({ path: { farm_id: params.farmId } }),
    );
    const annumsPromise = queryClient.ensureQueryData(
      getAnnumsForFarmOptions({ path: { farm_id: params.farmId } }),
    );
    const [farm, annums] = await Promise.all([farmPromise, annumsPromise]);

    return { farm, annums };
  };

// Router outlet context hook to get the farm/annum data in all child components.
export const useLoadedFarmData = () =>
  useOutletContext<{ farm: FarmGet; annums: FarmAnnums }>();

const FarmDataProvider = () => {
  const parentContext = useMobileNavOutletContext();
  const initialData = useLoaderData() as Awaited<
    ReturnType<ReturnType<typeof farmDataLoader>>
  >;
  const { farmId } = useParams();

  const { data: farm } = useRegenQuery(getFarmById, {
    path: { farm_id: farmId || "" },
    initialData: initialData?.farm,
  });
  const { data: annums } = useRegenQuery(getAnnumsForFarm, {
    path: { farm_id: farmId || "" },
    throwOnError: false,
    initialData: initialData?.annums,
  });

  // If there is no valid farmId, the endpoints above would return no data and we should not render
  // the app. This is not included above the queries because we can't conditionally call custom
  // hooks.
  if (!farmId) {
    return;
  }
  return (
    <div className="h-dvh" data-component="FarmDataProvider">
      <Outlet context={{ ...parentContext, farm, annums }} />
    </div>
  );
};

export default FarmDataProvider;
